import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Header,
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  ContentContainer,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardAction,
  Alert,
  AlertDescription,
  Badge,
  Button,
  Empty,
  EmptyTitle,
  EmptyDescription,
  Spinner,
  toast,
} from 'prosci-ui';
import { usePageContext } from '@/contexts/page-context';
import { AlertTriangle } from '@/lib/icons';
import { downloadTemplate } from './download-template';
import { guidedUseCaseService } from '@/services/guided-use-case-service';
import { blockerSummary, elementStatus } from './status';

export function KaiyaGuidedCanvasPage() {
  const { canvasKey } = useParams<{ canvasKey: string }>();

  const handleDownloadTemplate = async () => {
    try {
      const filename = await downloadTemplate(canvasKey);
      toast.success(`Downloaded ${filename}`);
    } catch {
      toast.error('Could not build the template');
    }
  };
  const { setShowPageHeader } = usePageContext();
  const queryClient = useQueryClient();

  useEffect(() => {
    setShowPageHeader(false);
    return () => setShowPageHeader(true);
  }, [setShowPageHeader]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['guidedCanvas', canvasKey],
    queryFn: () => guidedUseCaseService.getCanvas(canvasKey!),
    enabled: !!canvasKey,
  });

  const canvas = data?.data;
  const elements = canvas?.elements ?? [];
  const blocking = canvas?.blocking_elements ?? [];

  const visibilityMutation = useMutation({
    mutationFn: (publish: boolean) =>
      publish
        ? guidedUseCaseService.publishCanvas(canvasKey!)
        : guidedUseCaseService.unpublishCanvas(canvasKey!),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['guidedCanvas', canvasKey] });
      queryClient.invalidateQueries({ queryKey: ['guidedUseCases'] });
      toast.success(
        result.data.published
          ? 'This canvas is now offered to practitioners'
          : 'This canvas is no longer offered to practitioners'
      );
    },
    onError: (error: Error) => toast.error(error.message || 'Could not change visibility'),
  });

  const title = canvas?.title ?? canvasKey ?? '';

  return (
    <>
      <Header
        title={title}
        size="small"
        description={
          canvas
            ? `${canvas.element_count} elements. Each needs a definition of good before this canvas can be published.`
            : undefined
        }
        breadcrumb={
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/kaiya-guided-use-cases">Guided Use Cases</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{title}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        }
      />

      <ContentContainer>
        {isLoading ? (
          <Empty><Spinner /></Empty>
        ) : isError || !canvas ? (
          <Empty>
            <EmptyTitle>Canvas not found</EmptyTitle>
            <EmptyDescription>
              No guided use case is registered under "{canvasKey}".
            </EmptyDescription>
          </Empty>
        ) : (
          <>
            {blocking.length > 0 && (
              <Alert variant="warning">
                <AlertTriangle className="h-5 w-5" />
                <AlertDescription>
                  <strong>
                    {blocking.length} {blocking.length === 1 ? 'issue is' : 'issues are'}{' '}
                    blocking publication
                  </strong>
                  {blockerSummary(blocking)}
                </AlertDescription>
              </Alert>
            )}

            <Card>
              <CardHeader>
                <CardTitle>Elements</CardTitle>
                <CardAction>
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-muted-foreground">
                      {canvas.authored_count} of {canvas.element_count} defined
                    </span>
                    <Button size="sm" variant="outline" onClick={handleDownloadTemplate}>
                      Download this template
                    </Button>
                    {canvas.published ? (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={visibilityMutation.isPending}
                        onClick={() => visibilityMutation.mutate(false)}
                      >
                        Unpublish
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        disabled={!canvas.publishable || visibilityMutation.isPending}
                        onClick={() => visibilityMutation.mutate(true)}
                      >
                        Publish canvas
                      </Button>
                    )}
                  </div>
                </CardAction>
              </CardHeader>
              <CardContent className="p-0">
                <ul className="divide-y">
                  {elements.map((element, index) => {
                    const derived = element.source === 'derived';
                    const status = elementStatus(element);
                    const isBlocker = !derived && !element.authored;

                    return (
                      <li
                        key={element.scope_key}
                        className={`flex items-center gap-4 px-6 py-4 ${isBlocker ? 'bg-warning-50/40' : ''}`}
                      >
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted text-xs font-semibold text-muted-foreground">
                          {index + 1}
                        </span>

                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-sm">
                            {element.title ?? element.slot}
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            <span className="font-mono">{element.slot}</span>
                            {element.sub ? ` · ${element.sub}` : ''}
                          </div>

                          {derived && (
                            <div className="mt-2 text-xs text-muted-foreground">
                              Kaiya computes this from the other elements, so it needs no
                              definition of good.
                            </div>
                          )}

                          {isBlocker && (
                            <div className="mt-2 flex items-center gap-1.5 text-xs text-destructive">
                              <AlertTriangle className="h-4 w-4" />
                              No definition yet — this canvas cannot be offered until it has one
                            </div>
                          )}
                        </div>

                        <Badge variant={status.variant} tint>{status.label}</Badge>

                        {!derived && (
                          <Button
                            variant="outline"
                            size="sm"
                            className={element.authored ? '' : 'border-primary text-primary'}
                            asChild
                          >
                            <Link
                              to={`/kaiya-guided-use-cases/${element.canvas_key}/${element.slot}`}
                            >
                              {element.authored ? 'Edit' : 'Add definition'}
                            </Link>
                          </Button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </CardContent>
            </Card>
          </>
        )}
      </ContentContainer>
    </>
  );
}
